import type { CategoricalChartOptions } from './chartBuilder.types'

/** Class hook for the GA4 legend dot; tests and styles find the marker by it. */
export const GA4_LEGEND_DOT_CLASS = 'ga4-legend-dot'

// Small orange dot sitting top-right of the label like a superscript (v3 `GA4_DOT_HTML`), drawn
// with the brand-accent token rather than v3's raw hex.
const GA4_DOT_HTML = `<span aria-hidden="true" class="${GA4_LEGEND_DOT_CLASS} ml-0.5 inline-block size-1.5 flex-none self-start rounded-full bg-accent"></span>`

function escapeLegendText(value: unknown): string {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

interface LegendItemContext {
  name?: unknown
  userOptions?: { custom?: { metric?: unknown } }
}

/**
 * GA4 override marker (ORP-25550 / ORP-25673): every legend series whose metric took GA4 values
 * gets the orange dot next to its name. The explanation lives beside the chart (`Ga4Indicator`),
 * not inside the Highcharts legend, so opening it never redraws the chart. Legends that are off
 * or already own a `labelFormatter` (pie/donut point legends) are left alone.
 */
export function markGa4Legend(
  options: CategoricalChartOptions | null,
  fields: readonly string[] | undefined,
): CategoricalChartOptions | null {
  const legend = options?.legend
  if (!options || !fields?.length || !legend || legend.enabled === false) return options
  if (legend.labelFormatter) return options
  return {
    ...options,
    legend: {
      ...legend,
      useHTML: true,
      labelFormatter(this: LegendItemContext): string {
        const label = escapeLegendText(this.name)
        const metric = this.userOptions?.custom?.metric
        if (typeof metric !== 'string' || !fields.includes(metric)) return label
        return `<span class="inline-flex items-start">${label}${GA4_DOT_HTML}</span>`
      },
    },
  }
}
